import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const NlVersion = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='privacybeleid-voor-eindgebruikers'>
        Privacybeleid voor eindgebruikers
      </h4>

      <p>Beschikbare talen:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Ingangsdatum: 22 februari 2024</p>
      <p>
        Privacy en beveiliging zijn erg belangrijk voor ons bij Plaid. Dit
        Privacybeleid voor eindgebruikers ("Beleid") is bedoeld om u (de
        "eindgebruiker") te helpen begrijpen hoe wij bij Plaid uw gegevens
        verzamelen, gebruiken en delen wanneer u de producten of diensten van
        Plaid gebruikt – bijvoorbeeld wanneer u het Plaid Portal gebruikt, of
        wanneer u Plaid gebruikt om uw gegevens te koppelen en te delen om de
        toepassingen ("apps") die u gebruikt aan te sturen. Deze apps, die veel
        consumentendiensten en -producten bieden, zoals het helpen sparen voor
        uw pensioen, het beheren van uw uitgaven, het stroomlijnen van
        kredietaanvragen of het overboeken van geld, worden gebouwd en geleverd
        door onze zakelijke klanten (we noemen ze hier “ontwikkelaars”) en
        aangedreven door Plaid.
      </p>
      <p>
        Dit Beleid is van toepassing op Plaid Inc., Plaid Financial Ltd. en
        Plaid, B.V. (gezamenlijk “Plaid”, “wij”, “onze” en “ons”). (Zie de
        sectie “Contact opnemen met Plaid” hieronder voor welke van deze
        entiteiten verantwoordelijk is voor de verwerking van uw gegevens.) Dit
        Beleid heeft geen betrekking op diensten die worden aangeboden door
        Plaid Consumer Reporting Agency, Inc. (“Plaid CRA”). Ga voor meer
        informatie over dat beleid naar{' '}
        <Link href='/plaid-check-consumer-report/privacy-policy/'>
          <a>PolicyCRA Privacy</a>
        </Link>
        .
      </p>
      <p>
        U moet dit Beleid zorgvuldig lezen, het bevat belangrijke informatie
        over uw privacyrechten en -keuzes.{' '}
      </p>

      <p>
        <em>Spring naar onderdeel:</em>
      </p>

      <p>
        <Link href='#hoe-wij-omgaan-met-gegevens'>
          <a data-smooth-scroll data-offset='80'>
            Hoe wij omgaan met gegevens
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#gegevens-die-wij-verzamelen-en-broncategorieën'>
              <a data-smooth-scroll data-offset='80'>
                Gegevens die wij verzamelen en broncategorieën
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            {/* <Link href='#hoe-wij-uw-gegevens-gebruiken'> */}
            <Link href='#hoe-wij-uw-gegevens-gebruiken'>
              <a data-smooth-scroll data-offset='80'>
                Hoe we uw gegevens gebruiken
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#onze-rechtsgronden-voor-verwerking'>
              <a data-smooth-scroll data-offset='80'>
                Onze rechtsgronden voor verwerking (uitsluitend eindgebruikers
                in de EER en het Verenigd Koninkrijk)
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#hoe-we-uw-gegevens-delen'>
              <a data-smooth-scroll data-offset='80'>
                Hoe we uw gegevens delen
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#onze-bewaar-en-verwijderingspraktijken'>
              <a data-smooth-scroll data-offset='80'>
                Onze bewaar- en verwijderingspraktijken
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#bescherming-van-gegevens'>
              <a data-smooth-scroll data-offset='80'>
                Bescherming van gegevens
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        {/* <Link href='#verder-informatie-ter-afsluiting'> */}
        <Link href='#enkele-laatste-details'>
          <a data-smooth-scroll data-offset='80'>
            Enkele laatste details
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#internationale-gegevensoverdrachten'>
              <a data-smooth-scroll data-offset='80'>
                Internationale gegevensoverdrachten
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#uw-rechten-voor-gegevensbescherming'>
              <a data-smooth-scroll data-offset='80'>
                Uw rechten voor gegevensbescherming
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#uw-extra-privacycontroles'>
              <a data-smooth-scroll data-offset='80'>
                Uw extra privacycontroles
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#wijzigingen-in-dit-beleid'>
              <a data-smooth-scroll data-offset='80'>
                Wijzigingen in dit Beleid
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#contact-opnemen-met-plaid'>
              <a data-smooth-scroll data-offset='80'>
                Contact opnemen met Plaid
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#samenvattingen-van-verwerkingsactiviteiten'>
          <a data-smooth-scroll data-offset='80'>
            Samenvattingen van Verwerkingsactiviteiten
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#samenvatting-van-verwerkingsactiviteiten-per-informatiecategorie'>
              <a data-smooth-scroll data-offset='80'>
                Samenvatting van verwerkingsactiviteiten per informatiecategorie
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#samenvatting-van-verwerkingsactiviteiten-per-product'>
              <a data-smooth-scroll data-offset='80'>
                Samenvatting van verwerkingsactiviteiten per product
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#privacyverklaring-voor-consumenten'>
          <a data-smooth-scroll data-offset='80'>
            Privacyverklaring voor consumenten
          </a>
        </Link>
      </p>

      <h5 id='ten-eerste-wat-achtergrond'>Ten eerste, wat achtergrond</h5>
      <p>
        <em>Een korte opmerking over Plaid</em>
      </p>
      <p>
        Onze missie bij Plaid is het verwezenlijken van financiële vrijheid voor
        iedereen. Door toegang te bieden tot hoogwaardige, bruikbare financiële
        rekeninggegevens die wij vertaald en gestandaardiseerd hebben, stellen
        wij ontwikkelaars van financiële apps en diensten in staat om zich te
        richten op het bouwen van ervaringen waar u van profiteert.
      </p>
      <p>
        <em>Over dit Beleid</em>
      </p>
      <p>
        Ons doel met dit Beleid is om een eenvoudige en duidelijke uitleg te
        geven over welke gegevens Plaid van en over u verzamelt en hoe we die
        informatie gebruiken en delen. We waarderen transparantie en willen u
        een duidelijke en beknopte beschrijving geven van hoe we met uw gegevens
        omgaan.
      </p>
      <p>
        Dit Beleid dekt niet wat ontwikkelaars van de apps die u gebruikt doen
        met uw gegevens. Raadpleeg de privacybeleidslijnen of de
        servicevoorwaarden voor die apps voor informatie over hun praktijken.
        Dit Beleid heeft ook geen betrekking op gegevens die we verzamelen via
        onze websites of wanneer u met Plaid communiceert buiten het gebruik van
        onze producten of diensten, zoals het rechtstreeks e-mailen naar Plaid.
        Raadpleeg onze{' '}
        <Link href='#privacyverklaring'>
          <a> Privacyverklaring voor alle doelgroepen</a>
        </Link>{' '}
        en ons{' '}
        <Link href='#cookiebeleid'>
          <a>Cookiebeleid</a>
        </Link>{' '}
        voor meer informatie.
      </p>
      <p>
        De diensten van Plaid zijn niet gericht op personen jonger dan 18 jaar
        en we verzamelen niet bewust gegevens over kinderen.
      </p>

      <h5 id='hoe-wij-omgaan-met-gegevens'>Hoe wij omgaan met gegevens</h5>
      <p>
        Plaid zet zich in om eindgebruikers zinvolle controle over hun gegevens
        te bieden. Dit gedeelte beschrijft de gegevenspraktijken van Plaid met
        betrekking tot onze verwerking van data over u. We bieden ook
        samenvattingen van onze praktijken georganiseerd per categorie van
        verzamelde gegevens en per product aan het einde van dit Beleid in het
        gedeelte{' '}
        <Link href='#samenvattingen-van-verwerkingsactiviteiten'>
          <a>Samenvattingen van Verwerkingsactiviteiten</a>
        </Link>
        .
      </p>

      <h5 id='gegevens-die-wij-verzamelen-en-broncategorieën'>
        Gegevens die wij verzamelen en broncategorieën
      </h5>
      <p>
        Zoals hieronder in meer detail wordt uitgelegd, hangen de gegevens die
        we verzamelen, gebruiken en delen af van de producten en diensten van
        Plaid die u, en/of de app waarmee u verbonden bent, gebruikt.
        Afhankelijk van de producten of diensten van Plaid die u of de
        ontwikkelaar waarmee u verbinding maakt, gebruikt, kan Plaid de volgende
        gegevens verzamelen:
      </p>
      <ul>
        <li>
          <p>Gegevens die u aan ons verstrekt;</p>
        </li>
        <li>
          <p>
            Gegevens van financiële instellingen wanneer u uw financiële
            rekening koppelt;
          </p>
        </li>
        <li>
          <p>
            Gegevens van het elektronische apparaat dat u gebruikt om uw
            financiële rekening via Plaid te koppelen of om op een andere manier
            met Plaid te communiceren;
          </p>
        </li>
        <li>
          <p>
            Gegevens van de ontwikkelaar van de app waarmee u verbinding hebt
            gemaakt; en
          </p>
        </li>
        <li>
          <p>
            Gegevens van onze gelieerde ondernemingen om u ondersteuning te
            bieden of om u een betere gebruikerservaring te bieden; en/of
          </p>
        </li>
        <li>
          <p>
            Gegevens van andere bronnen, waaronder dienstverleners en diensten
            voor identiteitscontrole en fraudepreventie.
          </p>
        </li>
      </ul>
      <p>
        <strong>Gegevens die u aan ons verstrekt.</strong> Wanneer u de
        producten of diensten van Plaid gebruikt (zoals wanneer u Plaid gebruikt
        om uw financiële rekeningen met een app te koppelen), verzamelen we de
        volgende gegevens van u die nodig zijn om uw specifieke app of dienst
        aan te sturen:
      </p>
      <ul>
        <li>
          <p>
            <strong>identificatiegegevens</strong> zoals naam, e-mailadres, en
            telefoonnummer;
          </p>
        </li>
        <li>
          <p>
            <strong>aanmeldingsgegevens</strong> indien dit door de aanbieder
            van uw rekening wordt voorgeschreven, zoals uw gebruikersnaam en
            wachtwoord, rekening- en routeringsnummer, of een beveiligingstoken;
          </p>
        </li>
        <li>
          <p>
            <strong>
              alle aanvullende informatie die nodig is om uw rekeningen te
              koppelen
            </strong>
            , waaronder beveiligingsvragen en -antwoorden en een eenmalig
            wachtwoord (OTP); en/of
          </p>
        </li>
        <li>
          <p>
            informatie uit <strong>documenten of afschriften</strong>
            die u aan Plaid verstrekt, zoals bankafschriften of
            salarisafschriften.
          </p>
        </li>
      </ul>
      <p>
        Wanneer u inloggegevens en aanvullende informatie verstrekt die nodig
        zijn om uw rekeningen te koppelen, geeft u Plaid ook toestemming en
        autoriteit om namens u te handelen om toegang te krijgen tot gegevens en
        deze te verzenden van en naar uw financiële instelling.
      </p>

      <p>
        <strong>
          Gegevens die we verzamelen van financiële instellingen over en van uw
          rekeningen.
        </strong>{' '}
        Afhankelijk van welke producten of diensten van Plaid u, of de
        ontwikkelaar van uw app gebruikt, en welke, en hoe informatie
        beschikbaar wordt gesteld, kunnen wij de volgende gegevens van uw
        financiële instellingen verzamelen:
      </p>
      <ul>
        <li>
          <p>
            <strong>Rekeninggegevens</strong>,waaronder de naam van de
            financiële instelling, de naam van de rekening, het type rekening,
            de eigenaar van de rekening, het filiaalnummer, de IBAN, de BIC, het
            rekeningnummer, het routeringsnummer en de sorteercode;
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens over een rekeningsaldo</strong>, inclusief huidig
            en beschikbaar saldo;
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens over kredietrekeningen</strong>, waaronder
            vervaldata, verschuldigde saldi, betalingsbedragen en -data,
            transactiegeschiedenis, kredietlimiet, aflossingsstatus en
            rentevoet;
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens over leningrekeningen</strong>, waaronder
            vervaldata, aflossingsstatus, saldi, betalingsbedragen en -data,
            rentevoet, borg, type lening, betalingsregeling en voorwaarden;
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens over beleggingsrekeningen</strong>,waaronder
            transactiegegevens, het type activum, identificatiegegevens over het
            activum, hoeveelheid, prijs, vergoedingen en kostenbasis;
          </p>
        </li>
        <li>
          <p>
            <strong>Identificatiegegevens</strong> en gegevens over de
            rekeninghouder(s), waaronder naam, e-mailadres, telefoonnummer,
            geboortedatum en adresgegevens;
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens over rekeningtransacties</strong>, met inbegrip van
            bedrag, datum, begunstigde, type, hoeveelheid, prijs, locatie,
            betrokken effecten en een beschrijving van de transactie; en/of
          </p>
        </li>
        <li>
          <p>
            <strong>Gegevens uit uw loon- en belastingdocumenten</strong>,
            inclusief gegevens over uw inkomen en werkgever, in gevallen waarin
            u uw salarisrekeningen hebt aangesloten of ons uw salarisafschriften
            of gegevens van uw belastingformulier hebt verstrekt.
          </p>
        </li>
      </ul>
      <p>
        Afhankelijk van de dienst van Plaid die u of de ontwikkelaar van uw app
        gebruikt, en de manier waarop de gegevens beschikbaar worden gesteld,
        kunnen de verzamelde gegevens van uw financiële rekeningen gegevens
        bevatten van alle rekeningen (bijv. betaalrekeningen, spaarrekeningen,
        creditcardrekeningen en gezamenlijke rekeningen) die toegankelijk zijn
        via één set rekeninggegevens. Voor meer bijzonderheden over gegevens die
        worden verzameld in verband met verschillende producten en diensten,
        verwijzen wij u naar{' '}
        <Link href='#samenvatting-van-verwerkingsactiviteiten-per-product'>
          <a data-smooth-scroll data-offset='80'>
            Samenvatting van verwerkingsactiviteiten per product
          </a>
        </Link>
      </p>
      <p>
        <strong>Gegevens we ontvangen vanaf uw apparaten.</strong>Wanneer u een
        apparaat gebruikt, zoals uw smartphone, tablet of computer, voor
        interactie met onze diensten (inclusief via een app van een
        ontwikkelaar), kunnen wij de volgende gegevens over dat apparaat
        verzamelen:
      </p>
      <ul>
        <li>
          <p>internetprotocol (IP)-adres;</p>
        </li>
        <li>
          <p>tijdzone-instelling en locatie van het apparaat;</p>
        </li>
        <li>
          <p>hardwaremodel en besturingssysteem; </p>
        </li>
        <li>
          <p>functies binnen onze diensten waartoe u toegang hebt; </p>
        </li>
        <li>
          <p>browsergegevens; </p>
        </li>
        <li>
          <p>netwerkgegevens; en </p>
        </li>
        <li>
          <p>
            andere technische gegevens over het apparaat (zoals instellingen en
            voorkeuren).{' '}
          </p>
        </li>
      </ul>
      <p>
        <strong>
          Gegevens die we ontvangen over u van de ontwikkelaars van apps
          aangedreven door Plaid.{' '}
        </strong>{' '}
        Wanneer het nodig is voor Plaid om een dienst te verlenen (zoals het
        verifiëren van uw identiteit, het beschermen tegen fraude of risico's,
        of het mogelijk maken van een bankoverschrijving), kunnen de
        ontwikkelaars van de apps die u gebruikt ons identificatoren en
        commerciële informatie over u verstrekken, zoals uw naam,
        Burgerservicenummer, e-mailadres, telefoonnummer, of informatie over uw
        financiële rekeningen en transacties.
      </p>
      <p>
        <strong>
          Gegevens van onze gelieerde ondernemingen om u ondersteuning te bieden
          of om u een betere gebruikerservaring te bieden.{' '}
        </strong>
        We kunnen gegevens verzamelen van onze gelieerde ondernemingen (dit zijn
        bedrijven die met ons verbonden zijn door gemeenschappelijk eigendom of
        zeggenschap) om u ondersteuning te bieden of uw ervaring te verbeteren.
        Als u er bijvoorbeeld voor kiest dat Plaid CRA financiële rekeningen in
        verband met uw telefoonnummer onthoudt, kunnen wij de volgende gegevens
        van Plaid CRA verzamelen om u te helpen sneller verbinding te maken met
        uw rekeningen wanneer u onze diensten gebruikt:
      </p>
      <ul>
        <li>
          <p>
            <strong>identificatiegegevens </strong>
            zoals naam, e-mailadres en telefoonnummer;
          </p>
        </li>
        <li>
          <p>
            <strong>aanmeldingsgegevens </strong>
            indien dit door de aanbieder van uw rekening wordt voorgeschreven,
            zoals uw gebruikersnaam en wachtwoord, banknaam, rekening- en
            routeringsnummer, of een beveiligingstoken;
          </p>
        </li>
        <li>
          <p>
            indien nodig,{' '}
            <strong>
              gegevens om uw identiteit te helpen verifiëren en uw rekeningen te
              koppelen
            </strong>
            , waaronder uw geboortedatum, beveiligingsvragen en -antwoorden en
            een eenmalig wachtwoord (OTP); en/of gegevens van uw apparaat (zoals
            hierboven in meer detail uitgelegd).
          </p>
        </li>
        <li>
          <p>
            gegevens van uw apparaat (zoals hierboven in meer detail uitgelegd).
          </p>
        </li>
      </ul>
      <p>
        <strong>Gegevens die we over u ontvangen uit andere bronnen.</strong>{' '}
        Wanneer dit nodig is om een dienst te verlenen, uw apparaat te
        identificeren of fraude, misbruik of veiligheidsrisico's te helpen
        voorkomen, kunnen wij ook rechtstreeks gegevens over u ontvangen van
        derden, waaronder uw draadloze provider, agenten en onze
        dienstverleners.
      </p>
      <p>
        <strong>
          Informatie we afleiden van de gegevens die we verzamelen.
        </strong>{' '}
        We kunnen aanvullende informatie over u afleiden uit de gegevens die we
        verzamelen. Wij kunnen bijvoorbeeld uw geolocatie, jaarinkomen of het
        type rekening of subrekening dat u hebt gekozen om te koppelen afleiden
        – bijvoorbeeld wanneer u uw leningsrekeningen koppelt, zodat wij de
        ontwikkelaar kunnen laten weten of de rekening voor een hypotheek,
        studentenlening of creditcard is.
      </p>

      <h5 id='hoe-wij-uw-gegevens-gebruiken'>Hoe wij uw gegevens gebruiken</h5>
      <p>
        Wij gebruiken uw gegevens voor de volgende zakelijke en commerciële
        doeleinden:
      </p>
      <ul>
        <li>
          <p>
            <strong>Diensten verlenen: </strong> Om onze diensten uit te voeren,
            te leveren en te onderhouden.
          </p>
        </li>
        <li>
          <p>
            <strong>Bestaande diensten te ontwikkelen:</strong> Om onze diensten
            te verbeteren, wijzigen, aan te vullen en verder te ontwikkelen.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Helpen om fraude te voorkomen, uw identiteit te verifiëren of uw
              privacy te beschermen:
            </strong>{' '}
            Om uw identiteit te verifiëren en u, ontwikkelaars, onze partners,
            Plaid en anderen te helpen beschermen tegen fraude, kwaadaardige
            activiteiten en andere aan privacy- en veiligheid gerelateerde
            problemen.
          </p>
        </li>
        <li>
          <p>
            <strong>Nieuwe diensten te ontwikkelen:</strong> Om nieuwe producten
            en diensten te ontwikkelen.
          </p>
        </li>
        <li>
          <p>
            <strong>Inzichten te ontwikkelen: </strong> Om inzichten te
            ontwikkelen op basis van de gegevens die we over u hebben verzameld.
            Dit omvat uw transactiegegevens, andere financiële gegevens en
            gegevens uit andere bronnen, om ontwikkelaars van uw verbonden apps
            te helpen diensten en/of een betere gebruikerservaring aan u te
            leveren, zoals u sneller toegang tot uw geld geven, om mogelijk
            frauduleuze activiteiten te helpen opsporen en voorkomen, of om hun
            diensten aan u te personaliseren.
          </p>
        </li>
        <li>
          <p>
            <strong>Ondersteuning te bieden:</strong> Om aan u of aan
            ontwikkelaars ondersteuning te bieden, waaronder om te helpen
            reageren op uw vragen met betrekking tot onze diensten of apps van
            ontwikkelaars.
          </p>
        </li>
        <li>
          <p>
            <strong>Met u communiceren:</strong> Om met u te communiceren en u
            dingen te sturen zoals technische kennisgevingen, updates,
            beveiligingswaarschuwingen en berichten als u het Plaid Portal of
            Remember Me gebruikt.
          </p>
        </li>
        <li>
          <p>
            <strong>Misbruik en wangedrag te onderzoeken: </strong> Om misbruik
            van onze service of apps van ontwikkelaars te onderzoeken, met
            inbegrip van schendingen van ons Beleid voor{' '}
            <Link href='#developer-policy'>
              <a>ontwikkelaars</a>
            </Link>
            , criminele activiteiten of andere ongeoorloofde toegang tot onze
            services.
          </p>
        </li>
        <li>
          <p>
            <strong>Voor juridische doeleinden: </strong> Om te voldoen aan
            contractuele en wettelijke verplichtingen onder de toepasselijke
            wetgeving en voor andere juridische doeleinden, zoals het
            vaststellen van en verdedigen tegen claims.
          </p>
        </li>
        <li>
          <p>
            <strong>Met uw toestemming: </strong>Voor andere aangemelde
            doeleinden met uw toestemming of op uw aanwijzing.
          </p>
        </li>
      </ul>
      <p>
        Wij verzamelen, gebruiken en delen gegevens die zijn samengevoegd of
        geanonimiseerd op een manier die u niet persoonlijk identificeert voor
        elk doel dat is toegestaan onder de toepasselijke wetgeving. Dit omvat
        het creëren of gebruiken van samengevoegde of geanonimiseerde gegevens
        om nieuwe diensten te ontwikkelen of onderzoek te faciliteren, en voor
        analysedoeleinden om de snelheid, nauwkeurigheid en/of veiligheid van
        onze diensten te helpen beoordelen.{' '}
      </p>

      <h5 id='onze-rechtsgronden-voor-verwerking'>
        Onze rechtsgronden voor verwerking (uitsluitend eindgebruikers in de EER
        en het Verenigd Koninkrijk)
      </h5>
      <p>
        Voor personen in de Europese Economische Ruimte (“EER”) of het Verenigd
        Koninkrijk (“VK”), verwerkt Plaid uw persoonsgegevens alleen wanneer we
        een geldige rechtsgrond hebben om dit te doen. Onze rechtsgrondslag voor
        het verwerken van de gegevens die we verzamelen, is afhankelijk van
        welke gegevens we hebben verzameld en het doel van de verwerking. In het
        algemeen zullen wij uw gegevens alleen verzamelen en verwerken wanneer:
      </p>
      <ul>
        <li>
          <p>
            wij moeten voldoen aan een contract of overeenkomst met u
            (bijvoorbeeld om te voldoen aan onze overeenkomsten voor
            eindgebruikersdiensten) en moeten ons houden aan verschillende
            wettelijke voorschriften bij het aanbieden van gereguleerde
            diensten, waaronder onze rekeninginformatie en betalingsdiensten.
          </p>
        </li>
        <li>
          <p>
            we uw gegevens nodig hebben om te voldoen aan onze wettelijke
            verplichtingen onder de toepasselijke wetgeving, om de wettelijke
            rechten van Plaid te waarborgen en om criminele activiteiten zoals
            het witwassen van geld en fraude te voorkomen en te identificeren.
            Voor deze doeleinden kan Plaid het noodzakelijk vinden om uw
            persoonsgegevens te delen met entiteiten zoals rechtbanken,
            wetshandhavingsinstanties en aanbieders van antiwitwasdiensten;
          </p>
        </li>
        <li>
          <p>
            verwerking is noodzakelijk voor onze legitieme belangen om de
            integriteit van onze diensten effectief te handhaven. Dit omvat het
            ophalen van rekeninginformatie of het verlenen van
            betalingsinitiatiediensten (evenals het verifiëren van de output van
            deze activiteit), het aangaan van communicatie met u en het ervoor
            zorgen dat Plaid, als een gereglementeerde entiteit, de verwachte
            normen naleeft (waaronder die zijn vastgelegd door de Financial
            Conduct Authority voor Plaid Financial Ltd en De Nederlandsche Bank
            voor Plaid B.V); of
          </p>
        </li>
        <li>
          <p>u hiervoor uw toestemming hebt gegeven.</p>
        </li>
      </ul>
      <p>
        Voor zover wij vertrouwen op toestemming om uw gegevens te verzamelen en
        te verwerken, hebt u het recht om uw toestemming op elk gewenst moment
        in te trekken volgens de instructies in dit Beleid.
      </p>

      <h5 id='hoe-we-uw-gegevens-delen'>Hoe we uw gegevens delen</h5>
      <p>We delen uw gegevens om de volgende redenen:</p>
      <ul>
        <li>
          <p>
            Met de ontwikkelaar van de app die u gebruikt en zoals aangegeven
            door die ontwikkelaar;
          </p>
        </li>
        <li>
          <p>Om een contract met u uit te voeren;</p>
        </li>
        <li>
          <p>
            Met onze gegevensverwerkers en andere dienstverleners, partners,
            agenten of contractanten in verband met de diensten die zij voor ons
            of voor ontwikkelaars uitvoeren;
          </p>
        </li>
        <li>
          <p>
            Met financiële instellingen om een koppeling die u hebt gekozen
            tussen uw rekeningen bij een financiële instelling en een app tot
            stand te brengen, te onderhouden of te beheren;
          </p>
        </li>
        <li>
          <p>
            Als wij in goed vertrouwen geloven dat openbaarmaking gepast is om
            te voldoen aan de toepasselijke wet- en regelgeving of een juridisch
            proces (zoals een gerechtelijk bevel of dagvaarding);
          </p>
        </li>
        <li>
          <p>
            In verband met een verandering in eigenaarschap of controle van ons
            bedrijf of een deel ervan (zoals een fusie, overname, reorganisatie
            of faillissement);
          </p>
        </li>
        <li>
          <p>
            Tussen en binnen Plaid en onze huidige en toekomstige
            moedermaatschappijen, gelieerde bedrijven, dochterondernemingen en
            andere bedrijven onder gemeenschappelijke controle of eigenaarschap;
          </p>
        </li>
        <li>
          <p>
            Zoals wij redelijkerwijs passend achten om kwaadwillige of
            frauduleuze activiteiten te voorkomen, of op andere wijze de
            rechten, privacy, veiligheid of eigendommen van u, ontwikkelaars,
            onze partners, Plaid en anderen te beschermen; of
          </p>
        </li>
        <li>
          <p>
            Voor elk ander aangemeld doel met uw toestemming of op uw
            aanwijzing.
          </p>
        </li>
      </ul>
      <p>
        (Voor gebruikers in de VS) Wij delen uw gegevens niet met niet-gelieerde
        derden, tenzij dit wettelijk is toegestaan (zoals toegestaan door 12
        C.F.R. § 1016.14 en 1016.15).{' '}
      </p>
      <p>
        {' '}
        Wanneer u uw financiële rekeningen koppelt via Plaid, gebruiken we
        doorgaans de reCaptcha-dienst van Google om fraude en misbruik te helpen
        detecteren. Google reCAPTCHA is geïntegreerd in de producten en diensten
        van Plaid om ervoor te zorgen dat invoer in bijvoorbeeld online
        formulieren daadwerkelijk door echte personen wordt gedaan en niet door
        geautomatiseerde software (of bots).{' '}
      </p>
      <p>
        Google reCAPTCHA analyseert het gedrag van gebruikers van onze producten
        en services aan de hand van verschillende kenmerken. Google reCAPTCHA
        verwerkt persoonsgegevens zoals uw IP-adres, uw verblijfsduur op onze
        website en verdere informatie over uw gebruik van de producten en
        diensten van Plaid. Deze gegevens worden door Google verzonden en
        opgeslagen op servers in de Verenigde Staten en andere landen. Google
        heeft zich gecertificeerd voor het EU-VS Data Privacy Framework; het
        certificaat is{' '}
        <Link href='https://www.dataprivacyframework.gov/s/participant-search/participant-detail?id=a2zt000000001L5AAI&status=Active'>
          <a>here</a>
        </Link>{' '}
        beschikbaar. Wanneer reCAPTCHA wordt gebruikt, zijn het Privacybeleid en
        de Gebruiksvoorwaarden van Google van toepassing op reCAPTCHA en
        informatie die Google verzamelt via reCAPTCHA.
      </p>
      <p>
        We delen uw persoonlijke financiële gegevens alleen met derden om de
        door u gevraagde diensten te ondersteunen, wanneer u toestemming geeft
        en/of ter bescherming tegen fraude.
      </p>
      <p>
        We kunnen cookiegegevens verzamelen en delen met derden wanneer u onze
        website bezoekt, of we kunnen derden toestaan om deze cookiegegevens van
        onze websites te verzamelen. Raadpleeg ons{' '}
        <Link href='#cookiebeleid'>
          <a>Cookiebeleid</a>
        </Link>{' '}
        en het
        <Link href='#privacyverklaring'>
          <a> Privacybeleid</a>
        </Link>{' '}
        voor alle doelgroepen voor meer informatie.
      </p>

      <h5 id='onze-bewaar-en-verwijderingspraktijken'>
        Onze bewaar- en verwijderingspraktijken
      </h5>
      <p>
        Wij bewaren uw gegevens alleen zo lang als nodig is. Om te bepalen of de
        gegevens nodig zijn, houden we rekening met de reden waarom uw gegevens
        zijn verzameld en gebruikt en eventuele wettelijke voorschriften om uw
        gegevens te bewaren. We beoordelen uw gegevens periodiek om ervoor te
        zorgen dat ze nog steeds nodig zijn om te voldoen aan het doel waarvoor
        ze zijn verzameld of aan andere wettelijke verplichtingen. Als een
        ontwikkelaar uw verbinding van hun app met uw gegevens verwijdert, zijn
        de systemen van Plaid ontworpen om uw persoonsgegevens automatisch te
        verwijderen, behoudens bepaalde uitzonderingen waar we uw informatie nog
        steeds kunnen bewaren.
      </p>
      <p>
        Uitzonderingen hierop kunnen zijn als: (a) u via Plaid een verbinding
        met de app van een andere ontwikkelaar tot stand hebt gebracht die nog
        steeds actief is; (b) Plaid uw gegevens nodig heeft om u te blijven
        voorzien van een dienst van Plaid die u hebt aangevraagd; (c) Plaid
        wettelijk verplicht is om uw gegevens te bewaren; (d) Plaid uw gegevens
        nodig heeft om fraude te helpen voorkomen of de privacy te beschermen,
        ondersteuning te bieden of misbruik en wangedrag te onderzoeken; (e)
        Plaid uw gegevens zodanig heeft geanonimiseerd dat ze niet opnieuw
        geïdentificeerd kunnen worden of (f) wij vragen – en u gaat er specifiek
        mee akkoord – om ons toe te staan uw gegevens langer te bewaren.
      </p>
      <p>
        Uw gegevens worden alleen verwerkt zoals voorgeschreven door de wet of
        in overeenstemming met dit Beleid.
      </p>
      <p>
        Raadpleeg het gedeelte "Uw rechten voor gegevensbescherming" van dit
        Beleid voor de opties die voor u beschikbaar kunnen zijn, waaronder hoe
        u kunt verzoeken om uw gegevens te verwijderen. Het gedeelte "Uw
        privacybeheer" van dit Beleid bevat ook details over hulpmiddelen die
        voor u beschikbaar zijn om u te helpen bij het bekijken en beheren van
        de verbindingen die u met Plaid hebt gemaakt. U kunt ook contact met ons
        opnemen over onze praktijken voor het bewaren van gegevens met behulp
        van de contactgegevens in het gedeelte “Contact opnemen met Plaid”
        hieronder.
      </p>

      <h5 id='bescherming-van-gegevens'>Bescherming van gegevens</h5>
      <p>
        Het beveiligingsbeleid en de beveiligingspraktijken van Plaid zijn
        ontworpen om de vertrouwelijkheid en integriteit van uw gegevens te
        beschermen. Plaid implementeert controles die ontworpen zijn om de
        toegang tot deze gegevens te beperken tot personeel dat een zakelijke
        reden heeft om deze gegevens te kennen en verbiedt zijn personeel om
        deze gegevens op onwettige wijze te openen, te gebruiken of openbaar te
        maken.
      </p>

      <h5 id='enkele-laatste-details'>Enkele laatste details</h5>
      <h5 id='internationale-gegevensoverdrachten'>
        Internationale gegevensoverdrachten
      </h5>
      <p>
        Wij opereren internationaal en daarom geven wij de gegevens die wij over
        u verzamelen over internationale grenzen door voor verwerking en opslag
        (wij geven bijvoorbeeld gegevens door van de EER en het VK naar de
        Verenigde Staten en slaan gegevens op in AWS-regio's in de Verenigde
        Staten). Wanneer we gegevens doorgeven naar een ander land of gebied
        volgen we daarbij de toepasselijke wetgeving voor gegevensbescherming.
        In het bijzonder wanneer we gegevens vanuit de EER of het VK over andere
        internationale grenzen heen doorgeven, vertrouwen we op
        adequaatheidsbesluiten, overeenkomsten voor gegevensdoorgifte of andere
        door de EU-Commissie of de Britse staatssecretaris goedgekeurde (indien
        van toepassing) mechanismen voor dergelijke doorgiften, waaronder
        standaard contractuele clausules. U kunt een kopie van genoemde
        standaardcontractbepalingen aanvragen door contact met ons op te nemen
        zoals aangegeven onderaan deze Verklaring. Voorafgaand aan de doorgifte
        van gegevens vanuit de EER of het Verenigd Koninkrijk voeren we
        effectbeoordelingen uit en implementeren we eventuele aanvullende
        maatregelen om ervoor te zorgen dat alle overgedragen gegevens worden
        bewaard in overeenstemming met de voorschriften van de EER en het
        Verenigd Koninkrijk.
      </p>

      <h5 id='uw-rechten-voor-gegevensbescherming'>
        Uw rechten voor gegevensbescherming
      </h5>
      <p>
        Ongeacht waar u woont, zullen we de volgende rechten met betrekking tot
        uw persoonsgegevens respecteren, met inachtneming van enkele beperkingen
        en uitzonderingen die door de wet worden voorzien, en u wordt niet
        gediscrimineerd voor het uitoefenen ervan:
      </p>
      <ul>
        <li>
          <p>toegang te krijgen tot gegevens die over u zijn verzameld;</p>
        </li>
        <li>
          <p>
            toegang vragen tot meer details over de categorieën en specifieke
            persoonsgegevens die wij in de afgelopen 12 maanden over u hebben
            verzameld (inclusief persoonsgegevens die voor zakelijke doeleinden
            zijn vrijgegeven);
          </p>
        </li>
        <li>
          <p>
            onder bepaalde omstandigheden verzoeken dat wij uw gegevens die
            onnauwkeurig of onvolledig zijn, corrigeren of bijwerken;
          </p>
        </li>
        <li>
          <p>
            onder bepaalde omstandigheden verzoeken dat wij uw gegevens te
            wissen of de verwerking ervan beperken;
          </p>
        </li>
        <li>
          <p>
            bezwaar te maken tegen onze verwerking van uw gegevens onder
            bepaalde voorwaarden die door de wet worden bepaald;
          </p>
        </li>
        <li>
          <p>
            wanneer de verwerking van uw gegevens is gebaseerd op toestemming,
            dient u die toestemming in te trekken;
          </p>
        </li>
        <li>
          <p>
            verzoeken dat wij de over u verzamelde gegevens verstrekken in een
            gestructureerd, algemeen gebruikt en machinaal leesbaar formaat,
            zodat u deze kunt overdragen aan een ander bedrijf, indien dit
            technisch haalbaar is.
          </p>
          <ul>
            <li>
              <p>
                Houd er rekening mee dat u voor een officiële registratie van uw
                financiële informatie dat verzoek rechtstreeks bij uw financiële
                instelling moet indienen.{' '}
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Afhankelijk van waar u woont, hebt u mogelijk het recht om een klacht in
        te dienen. We verwelkomen en waarderen de kans om eventuele zorgen die u
        hebt aan te pakken en moedigen u aan om contact met ons op te nemen.
        Bovendien, en afhankelijk van uw rechtsgebied, kunt u het recht hebben
        om op elk moment een klacht in te dienen bij uw
        (gegevensbeschermings-)toezichthoudende autoriteit. Voor eindgebruikers
        in de EER vindt u de contactgegevens van het{' '}
        <Link href='https://edpb.europa.eu/about-edpb/about-edpb/members_en'>
          <a>Europees Comité voor gegevensbescherming (EDPB)</a>
        </Link>{' '}
        op de website van het EDPB. Voor eindgebruikers in het Verenigd
        Koninkrijk vindt u de contactgegevens van het{' '}
        <Link href='https://ico.org.uk/'>
          <a>Information Commissioner’s Office (ICO)</a>
        </Link>{' '}
        op de website van het ICO. Voor eindgebruikers in Zwitserland is dit de{' '}
        <Link href='https://www.edoeb.admin.ch/edoeb/en/home.html'>
          <a>Federal Data Protection and Information Commissioner</a>
        </Link>{' '}
        die u kunt vinden.
      </p>

      <p>
        {' '}
        Om rechten uit te oefenen, kunt u een verzoek indienen via ons{' '}
        <Link href='https://my.plaid.com/data-subject-request-form-nl'>
          <a>online formulier</a>
        </Link>
        . U kunt ook contact met ons opnemen zoals beschreven in het gedeelte
        "Contact opnemen met Plaid" hieronder om uw rechten op het gebied van
        gegevensbescherming uit te oefenen. Het kan zijn dat u aanvullende
        gegevens moet verstrekken om uw identiteit te bevestigen voordat wij op
        uw verzoek kunnen reageren.
      </p>
      <p>
        Wanneer wij uw verzoek ontvangen van een gevolmachtigde agent, kunnen
        wij om bewijs vragen dat de agent een geldige schriftelijke machtiging
        heeft, zoals een volmacht, om namens u verzoeken in te dienen.
      </p>
      <p>
        We zullen verzoeken in overweging nemen en ons antwoord geven binnen een
        redelijke termijn (en binnen een door de toepasselijke wetgeving
        voorgeschreven termijn). Wij wijzen u er echter op dat bepaalde gegevens
        van dergelijke verzoeken kunnen worden vrijgesteld, bijvoorbeeld als wij
        de gegevens moeten bewaren om aan onze eigen wettelijke verplichtingen
        te voldoen of om rechtsvorderingen in te stellen, uit te oefenen of te
        verdedigen.
      </p>
      <p>
        Voor zowel Plaid Financial Ltd. als Plaid B.V. hebben wij een
        functionaris voor gegevensbescherming (DPO) aangesteld, die ook
        verantwoordelijk is voor het toezicht op vragen met betrekking tot dit
        Beleid en waarmee contact kan worden opgenomen via e-mail op{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>

      <h5 id='uw-extra-privacycontroles'>Uw extra privacycontroles</h5>
      <p>
        Plaid heeft het{' '}
        <Link href='http://my.plaid.com'>
          <a>Plaid Portal</a>
        </Link>{' '}
        ontwikkeld om u een handige, gecentraliseerde manier te bieden om de
        verbindingen die u met Plaid hebt gemaakt te bekijken en te beheren.
      </p>
      <p>
        Als u in de VS gevestigd bent, dan kunt u een Plaid Portal-account
        aanmaken door naar my.plaid.com te gaan, uw telefoonnummer en
        e-mailadres te verifiëren en een wachtwoord aan te maken. Zodra u zich
        hebt aangemeld voor het Plaid Portal, wordt u naar een dashboard geleid
        dat u financiële rekeningen kan laten zien die u via Plaid met de door u
        gekozen apps hebt verbonden, en de soorten gegevens die met die apps
        worden gedeeld. Bovendien biedt het Plaid Portal u controles om de
        verbinding tussen apps en uw financiële rekeningen te beëindigen en
        bijbehorende gegevens die zijn opgeslagen in de systemen van Plaid te
        verwijderen.
      </p>

      <h5 id='wijzigingen-in-dit-beleid'>Wijzigingen in dit Beleid</h5>
      <p>
        We kunnen dit Beleid van tijd tot tijd bijwerken of wijzigen. Als we
        updates of wijzigingen aanbrengen, plaatsen we het nieuwe beleid op de
        website van Plaid op{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>{' '}
        en werken we de ingangsdatum bovenaan dit Beleid bij. Wij zullen
        ontwikkelaars ook op de hoogte stellen van materiële wijzigingen in
        overeenstemming met onze overeenkomsten met ontwikkelaars, aangezien zij
        beter in staat kunnen zijn om u op de hoogte te stellen van wijzigingen
        in dit Beleid. Als u een actieve gebruiker van Plaid Portal bent, sturen
        wij u ook een e-mail om u op de hoogte te stellen van de update.
      </p>

      <h5 id='contact-opnemen-met-plaid'>Contact opnemen met Plaid</h5>
      <p>
        Als u vragen of klachten hebt over dit Beleid, of over ons
        privacypraktijken in het algemeen, kunt u contact met ons opnemen via{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        of een brief sturen naar:
      </p>
      <p>
        <em>
          Indien u woonachtig bent buiten de EER of het Verenigd Koninkrijk:
        </em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc.
        <br />
        Attn: Legal
        <br />
        PO Box 7775 #35278
        <br />
        San Francisco, California 94120-7775
        <br />
        VS
      </div>
      <br />
      <br />
      <p>
        Voor zowel Plaid Financial Ltd. als Plaid, B.V. hebben wij een
        functionaris voor gegevensbescherming (DPO) aangesteld, die ook
        verantwoordelijk is voor het toezicht op vragen met betrekking tot dit
        Beleid en waarmee contact kan worden opgenomen via e-mail op
        privacy@plaid.com.{' '}
      </p>
      <p>
        <br />
        <em>Als u in het Verenigd Koninkrijk woont:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd.
        <br />
        Attn: Legal
        <br />
        New Penderel House, 4th Floor
        <br />
        283-288 High Holborn
        <br />
        London, United Kingdom, WC1V 7HP
      </div>
      <p>
        <br />
        <em>Als u in de EER woont:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid B.V.
        <br />
        t.a.v.: Juridische afdeling
        <br />
        Muiderstraat 1
        <br />
        1011 PZ Amsterdam
        <br />
        Netherlands
      </div>
      <br />
      <br />

      <h5 id='samenvattingen-van-verwerkingsactiviteiten'>
        Samenvattingen van Verwerkingsactiviteiten
      </h5>

      <h5 id='samenvatting-van-verwerkingsactiviteiten-per-informatiecategorie'>
        Samenvatting van verwerkingsactiviteiten per informatiecategorie
      </h5>
      <p>
        Om nog meer transparantie over onze praktijken te bieden, hebben we de
        informatie in ons Beleid hierboven samengevoegd in een tabel die de
        categorieën gegevens die Plaid over u verzamelt koppelt aan de bronnen
        van de gegevens, het gebruik van de gegevens en de categorieën
        ontvangers met wie Plaid de gegevens deelt. Voor meer gedetailleerde
        beschrijvingen van onze praktijken met betrekking tot het verzamelen,
        gebruiken en delen van gegevens, verwijzen wij u naar de bovenstaande
        secties van het Beleid.
      </p>

      <br />
      <br />
      <div className='eupp-container'>
        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Categorie van persoonsgegevens en voorbeelden</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Bron van persoonsgegevens</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Gebruik van persoonsgegevens </span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Categorieën ontvangers met wie persoonsgegevens kunnen
                    worden gedeeld
                  </span>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Identificatiegegevens</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: naam, e-mailadres, telefoonnummer en
                    gebruikersnaam
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Gegevens die u verstrekt</span>
                  </li>
                  <li>
                    <span>
                      Gegevens die wij verzamelen van uw financiële rekeningen
                    </span>
                  </li>
                  <li>
                    <span>Gegevens die we ontvangen vanaf uw apparaten</span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen van de ontwikkelaars van
                      apps die worden aangestuurd door Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens van onze gelieerde ondernemingen om u diensten of
                      ondersteuning te bieden
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen uit andere bronnen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van app(s) die u gebruikt, en zoals
                      aangegeven door de ontwikkelaar(s)
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>Aanbieders van betaaldiensten </span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>
                      Aanbieders van diensten voor identiteitscontrole
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                  <li>
                    <span>
                      Externe partijen indien daartoe een wettelijke
                      verplichting bestaat, bijvoorbeeld aan wetshandhavings- en
                      andere overheidsinstanties
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Financi&euml;le Gegevens</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    bijvoorbeeld: naam en nummer financi&euml;le rekening, saldo
                    en transactie-geschiedenis
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Gegevens die u verstrekt</span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen van de ontwikkelaars van
                      apps die worden aangestuurd door Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die wij verzamelen van uw financiële rekeningen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen uit andere bronnen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we afleiden van gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>Aanbieders van betaaldiensten </span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid
                    </span>
                    <span>&nbsp;</span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>
                      Aanbieders van diensten voor identiteitscontrole
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                  <li>
                    <span>
                      Externe partijen indien daartoe een wettelijke
                      verplichting bestaat, bijvoorbeeld tegenover
                      wetshandhavings- en andere overheidsinstanties
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Commerciële gegevens</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: gegevens met betrekking tot welke van onze
                    diensten u gebruikt via ontwikkelaarsapps en de datums en
                    tijden van uw gebruik
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Gegevens die wij verzamelen van uw financiële rekeningen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen van de ontwikkelaars van
                      apps die worden aangestuurd door Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens van onze gelieerde ondernemingen om u diensten of
                      ondersteuning te bieden
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen uit andere bronnen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we afleiden van gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                  <li>
                    <span>Gegevens die we ontvangen vanaf uw apparaten</span>
                  </li>
                  <li>
                    <span>
                      Gegevens van onze gelieerde ondernemingen om u diensten of
                      ondersteuning te bieden
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we afleiden van gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>Aanbieders van betaaldiensten </span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>
                      Aanbieders van diensten voor identiteitscontrole
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                  <li>
                    <span>
                      Externe partijen indien daartoe een wettelijke
                      verplichting bestaat, bijvoorbeeld wetshandhavings- en
                      andere overheidsinstanties
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Locatiegegevens</span>
                </p>
                <p>
                  <span>&nbsp; &nbsp; &nbsp;</span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: tijdzone-instelling en locatie van het
                    apparaat
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Gegevens die we ontvangen vanaf uw apparaten</span>
                  </li>
                  <li>
                    <span>
                      Gegevens van onze gelieerde ondernemingen om u diensten of
                      ondersteuning te bieden
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we afleiden van gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                  <li>
                    <span>
                      Externe partijen indien daartoe een wettelijke
                      verplichting bestaat, bijvoorbeeld wetshandhavings- en
                      andere overheidsinstanties
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Professionele gegevens</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: informatie over uw werkgever en
                    salarisgegevens
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Gegevens die u verstrekt</span>
                  </li>
                  <li>
                    <span>
                      Gegevens die wij verzamelen van uw financiële rekeningen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we over u ontvangen uit andere bronnen
                    </span>
                  </li>
                  <li>
                    <span>
                      Gegevens die we afleiden van gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>
                      Aanbieders van diensten voor identiteitscontrole
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Gegevens over elektronische netwerkactiviteit</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: het model en besturingssysteem van uw
                    apparaat en de browsergegevens
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <p>
                  <span></span>
                </p>
                <ul>
                  <li>
                    <span>Gegevens die we ontvangen vanaf uw apparaten</span>
                  </li>
                  <li>
                    <span>
                      Gegevens van onze gelieerde ondernemingen om u diensten of
                      ondersteuning te bieden
                    </span>
                  </li>
                </ul>
                <p>
                  <span></span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken</span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Informatie die we ontlenen</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    Bijvoor-beeld: we kunnen een locatie afleiden van het
                    IP-adres of uw jaarinkomen van uw loonstroken
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Informatie die we verkrijgen uit gegevens die we van u of
                      andere bronnen verzamelen
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Diensten verlenen</span>
                  </li>
                  <li>
                    <span>Bestaande diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>
                      Fraude helpen voorkomen of privacy en veiligheid
                      beschermen
                    </span>
                  </li>
                  <li>
                    <span>Nieuwe diensten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Inzichten ontwikkelen</span>
                  </li>
                  <li>
                    <span>Ondersteuning bieden</span>
                  </li>
                  <li>
                    <span>Misbruik en overtredingen onderzoeken </span>
                  </li>
                  <li>
                    <span>Voor juridische doeleinden</span>
                  </li>
                  <li>
                    <span>Met uw toestemming</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Ontwikkelaar(s) van toepassing(en) die u gebruikt, en
                      zoals aangegeven door de ontwikkelaar
                    </span>
                  </li>
                  <li>
                    <span>Financi&euml;le instellingen</span>
                  </li>
                  <li>
                    <span>Aanbieders van betaaldiensten </span>
                  </li>
                  <li>
                    <span>
                      Partners en contractanten in verband met de diensten die
                      zij voor ons of voor ontwikkelaars verrichten
                    </span>
                  </li>
                  <li>
                    <span>Professionele adviseurs</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners die Plaid helpen gegevens te analyseren
                      voor zakelijke doeleinden van Plaid{' '}
                    </span>
                  </li>
                  <li>
                    <span>Leveranciers van cloudopslagdiensten</span>
                  </li>
                  <li>
                    <span>Aanbieders van softwareontwikkelingsdiensten</span>
                  </li>
                  <li>
                    <span>
                      Dienstverleners op het gebied van fraudepreventie
                    </span>
                  </li>
                  <li>
                    <span>Entiteiten van de Plaid-groep</span>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='samenvatting-van-verwerkingsactiviteiten-per-product'>
        Samenvatting van verwerkingsactiviteiten per product
      </h5>
      <p>
        Zoals hierboven vermeld, zijn de gegevens die we verzamelen en gebruiken
        afhankelijk van de producten en diensten van Plaid die u en de
        ontwikkelaar gebruiken. Om het makkelijker te maken om te begrijpen
        welke gegevens voor welk(e) product en service worden verzameld en
        gebruikt, hebben we de onderstaande samenvattingen van onze
        gegevensverzameling en -gebruik per product samengesteld. Sommige van
        deze producten zijn alleen beschikbaar voor gebruikers in de VS en
        andere zijn alleen beschikbaar voor gebruikers in de EER en het Verenigd
        Koninkrijk. Als u niet zeker weet welke Plaid-producten of -diensten uw
        verbonden toepassingen gebruiken, neem dan contact op met de
        ontwikkelaar van de toepassing. (Let erop dat deze lijst de productsuite
        van Plaid weergeeft vanaf de ingangsdatum van dit Beleid en mogelijk
        geen producten of diensten bevat die op die datum in ontwikkeling zijn.)
      </p>
      <p>
        Daarnaast maakt de wetgeving voor gegevensbescherming in bepaalde
        rechtsgebieden, zoals het VK en de EU, onderscheid tussen de
        “verwerkingsverantwoordelijke” en “verwerker” van gegevens. Afhankelijk
        van welk(e) product of dienst u en uw ontwikkelaar gebruiken, kan Plaid
        de verwerkingsverantwoordelijke of de verwerker zijn van de gegevens die
        zijn verzameld onder de relevante wetgeving voor gegevensbescherming.
        Tenzij anders vermeld in de onderstaande productbeschrijving, is Plaid
        de verwerkingsverantwoordelijke van de verzamelde persoonsgegevens.{' '}
      </p>

      <h5>Assets</h5>
      <p>
        Met Assets kunt u toegang verlenen tot een point-in-time weergave van de
        gegevens van uw gekozen financiële rekeningen, zoals rekeningsaldi,
        transactiegeschiedenis en identiteitsgegevens van de rekeninghouder, aan
        Plaid en de apps en diensten die u kiest. We verzamelen identificatoren,
        financiële informatie, commerciële gegevens, locatiegegevens en gegevens
        over elektronische netwerkactiviteiten en wij leiden hieruit conclusies
        af. Wij gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe we uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Auth</h5>
      <p>
        Auth helpt om u in staat te stellen uw financiële rekeninggegevens te
        verifiëren en Plaid en de apps en diensten die u kiest toegang te
        verlenen tot uw rekening- en routeringsnummers. We verzamelen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens en elektronische netwerkactiviteiten. Wij gebruiken deze
        gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Balance</h5>
      <p>
        Met Balance en de optionele functies ervan kunt u Plaid en de apps en
        diensten die u kiest toegang verlenen tot uw financiële rekeningsaldi in
        realtime en/of afgeleide conclusies uit uw rekeningsaldi en andere
        rekeninginformatie, zodat ze u kunnen helpen om bijvoorbeeld te
        voorkomen dat er te veel op uw rekening staat voordat u een overboeking
        maakt. We verzamelen identificatoren, financiële gegevens, commerciële
        gegevens, locatiegegevens en gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit in sommige gevallen conclusies
        af. Wij gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Identiteit en identiteitsmatch </h5>
      <p>
        Met Identity kunt u Plaid en de apps en diensten die u kiest toegang
        verlenen tot de rekeninghoudergegevens die in het bezit zijn van uw
        financiële instelling, zoals uw naam, e-mailadres, telefoonnummer en
        postadres, om hen te helpen bij zaken als het verifiëren van uw
        identiteit of het vooraf invullen van uw rekeninggegevens binnen de app.
        Identity Match helpt de apps en diensten die u gebruikt om uw identiteit
        te verifiëren aan de hand van gegevens van rekeninghouders die in het
        bezit zijn van uw financiële instelling. We verzamelen identificatoren,
        financiële gegevens, commerciële gegevens, locatiegegevens en
        elektronische netwerkactiviteiten. Wij gebruiken deze gegevens zoals
        beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5> Verificatie betaalkaart</h5>
      <p>
        Betaalkaartverificatie geeft u toegang tot de gegevens van uw
        rekeninghouder, het saldo van uw financiële rekening in realtime en de
        transactiegeschiedenis om te bevestigen dat de betaalkaart die u
        gebruikt, gekoppeld is aan een bankrekening waarvan u de eigenaar bent.
        We verzamelen identificatoren, financiële gegevens, commerciële
        gegevens, locatiegegevens en gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit in sommige gevallen conclusies
        af. Wij gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Verificatie en controle van identiteit</h5>
      <p>
        Identiteitsverificatie en -controle (IDV) maken identiteitsverificatie,
        fraudedetectie en controlediensten voor ontwikkelaars mogelijk. Wanneer
        we diensten voor identiteitsverificatie en -controle leveren aan
        ontwikkelaars, verzamelen en verwerken we alleen uw gegevens als
        dienstverlener aan die ontwikkelaar, zodat de ontwikkelaars het doel en
        de middelen bepalen waarmee uw gegevens worden verwerkt. Ga voor meer
        informatie over hoe we eindgebruikersgegevens verwerken voor
        ontwikkelaars en over de instructie van de ontwikkelaar om
        identiteitsverificatie- en monitoringsdiensten te leveren naar de{' '}
        <Link href='https://cognitohq.com/privacy-statement'>
          <a>Privacyverklaring voor eindgebruikers</a>
        </Link>{' '}
        van IDV.
      </p>

      <h5>Plaid Account</h5>
      <p>
        Met Plaid Account kunt u zich inschrijven voor een snellere terugkerende
        gebruikerservaring door Plaid toe te staan uw financiële
        rekeningverbindingen en identiteitsgegevens op te slaan voor een
        snellere onboarding en gebruik van de door u gekozen apps. We verzamelen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens en elektronische netwerkactiviteiten. Wij gebruiken deze
        gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#how-we-use-your-data'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Income</h5>
      <p>
        Income stelt u in staat om Plaid en de apps en diensten die u kiest
        toegang te verlenen tot gegevens met betrekking tot uw inkomen en werk.
        Wij verzamelen identificatoren, financiële gegevens, commerciële
        gegevens, locatiegegevens, gegevens over werk, inkomen en andere
        professionele gegevens en gegevens over elektronische
        netwerkactiviteiten en wij leiden conclusies af uit deze gegevens. Wij
        gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Investments </h5>
      <p>
        Met Investments kunt u Plaid en de apps of diensten die u kiest toegang
        verlenen tot gegevens van uw pensioen-, makelaars-, studie- en
        gezondheidsspaarrekeningen, zodat zij u bijvoorbeeld persoonlijke
        financiële en tools voor vermogensbeheer kunnen bieden. We verzamelen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens, professionele gegevens, gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit conclusies af. Wij gebruiken
        deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Liabilities </h5>
      <p>
        Met Liabilities kunt u Plaid en de apps en diensten die u kiest toegang
        geven tot financiële gegevens van uw creditcard-, hypotheek- en
        studierekeningen. We verzamelen identificatoren, financiële gegevens,
        commerciële gegevens, locatiegegevens, gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit conclusies af. Wij gebruiken
        deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>
      <h5>Payment Initiation en Variable Recurring Payments </h5>
      <p>
        Met Payment Initiation kunnen gebruikers van diensten in de EU en het VK
        eenmalige, terugkerende of variabel terugkerende betalingen doen op
        rekeningen. Dit geldt voor diensten zoals bankoverschrijvingen,
        doorlopende opdrachten en variabele terugkerende betalingen en eenmalige
        of terugkerende betalingen van rekeningen, zoals opnames en
        terugbetalingen. Met Variable Recurring Payments kunnen onze EU- en
        Britse gebruikers eenmalig een bankrekening verifiëren, en vervolgens
        toekomstige betalingen uitvoeren zonder extra verificatie. We verzamelen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens, gegevens over elektronische netwerkactiviteiten en wij
        leiden hieruit conclusies af. Wij gebruiken deze gegevens zoals
        beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>
      <h5>Payouts </h5>
      <p>
        Met Payouts kunnen gebruikers van diensten in de EU en het VK naadloos
        en direct opnames en terugbetalingen doen vanaf een app of service die u
        gebruikt, door een e-geldrekening te openen en vooraf te financieren van
        waaruit geld kan worden verzonden naar gebruikers die een uitbetaling
        aanvragen. We verzamelen identificatoren, financiële gegevens,
        commerciële gegevens, locatiegegevens, gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit conclusies af. Wij gebruiken
        deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>
      <h5>Plaid Portal</h5>
      <p>
        Plaid Portal is een privacytool die u kunt gebruiken om de verbindingen
        met apps die u via Plaid hebt gemaakt te beheren. U kunt Plaid Portal
        gebruiken om verbindingen met apps in één dashboard te bekijken,
        financiële rekeningen los te koppelen van apps of gegevens uit de
        systemen van Plaid te verwijderen. U kunt het Plaid Portal ook gebruiken
        om uw Plaid rekeningen te bekijken en te beheren. We verzamelen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens en elektronische netwerkactiviteiten. Wij gebruiken deze
        gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Signal</h5>
      <p>
        Voor klanten in de VS helpt Signal de apps en diensten die u gebruikt om
        u een betere gebruikerservaring te bieden, zoals snellere toegang tot uw
        geld en vermindering van het risico op fraude of een betaling die niet
        wordt voltooid. We verzamelen identificatoren, financiële gegevens,
        commerciële gegevens, locatiegegevens, gegevens over elektronische
        netwerkactiviteiten en wij leiden hieruit conclusies af. Wij gebruiken
        deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <h5>Statements</h5>
      <p>
        Met Afschriften kunt u kopieën van uw financiële rekeningafschriften
        delen met Plaid en de apps en diensten die u kiest. Wij kunnen
        identificatoren, financiële gegevens, commerciële gegevens,
        locatiegegevens en gegevens over elektronische netwerkactiviteiten
        verzamelen. Wij gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>
      <h5>Transactions </h5>
      <p>
        Met Transactions en aanverwante producten kunt u Plaid en door u gekozen
        apps en diensten toegang verlenen tot transactiegeschiedenissen van uw
        financiële rekeningen, zodat zij u dingen kunnen bieden zoals tools voor
        persoonlijk financieel beheer, onkostendeclaraties, cashflowmodellering
        en meer. Wij verzamelen mogelijk identificatoren, financiële gegevens,
        commerciële gegevens, locatiegegevens en gegevens over elektronische
        netwerkactiviteiten, en wij leiden uit deze gegevens conclusies en
        financiële inzichten af. Wij gebruiken dit zoals hierboven
        gespecificeerd in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>
      <h5>Transfer </h5>
      <p>
        Met Transfer kunt u een ontwikkelaar machtigen om op eenmalige of
        terugkerende basis betalingen naar u te sturen of van u te innen via
        bankoverschrijvingen. Dit omvat het controleren of u de eigenaar bent
        van de rekening waarnaar of van waaruit de betaling wordt verzonden, het
        inschatten van het risico dat de betaling niet wordt voltooid of
        frauduleus is, het elektronisch verwerken van betalingsinstructies en
        het leveren van een bevestiging aan de ontwikkelaar dat de betaling is
        voltooid. We kunnen identificatoren, financiële, commerciële en
        locatiegegevens en elektronische netwerkactiviteiten verzamelen. Wij
        gebruiken deze gegevens zoals beschreven in het gedeelte{' '}
        <Link href='#hoe-wij-uw-gegevens-gebruiken'>
          <a>Hoe wij uw gegevens gebruiken</a>
        </Link>{' '}
        van dit Beleid.
      </p>

      <br />
      <hr className='section-divider' />

      <h5 id='privacyverklaring-voor-consumenten'>
        Privacyverklaring voor consumenten
      </h5>
      <p>
        <span>Laatst bijgewerkt: </span>
        <span> 22 februari 2024</span>
      </p>
      <table className='eupp-table'>
        <thead>
          <tr>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span></span>
              </p>
            </td>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>WAT DOET PLAID MET UW PERSOONLIJKE INFORMATIE?</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Waarom?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Financiële bedrijven kiezen hoe ze uw persoonlijke informatie
                  delen. De federale wetgeving geeft consumenten het recht om
                  het delen van sommige, maar niet alle informatie, te beperken.
                  De federale wetgeving vereist ook dat we u vertellen hoe we uw
                  persoonlijke informatie verzamelen, delen en beschermen. Lees
                  deze kennisgeving zorgvuldig door om te begrijpen wat we doen.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Wat?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  De soorten persoonlijke informatie die we verzamelen en delen,
                  zijn afhankelijk van het product of de dienst die u of de
                  app-ontwikkelaar waarmee u verbinding maakt, gebruikt. Deze
                  informatie kan het volgende omvatten:
                </span>
              </p>
              <ul>
                <li>
                  <span>Burgerservicenummer en rekeningsaldi</span>
                </li>
                <li>
                  <span>Rekeningtransacties en transactiegeschiedenis</span>
                </li>
                <li>
                  <span>Informatie over inkomen en werkgelegenheid</span>
                </li>
              </ul>
              <p>
                <span>
                  Raadpleeg voor meer informatie over de soorten persoonlijke
                  informatie die we verzamelen het gedeelte{' '}
                  <Link href='#gegevens-wij-verzamelen-en-categorieën-bronnen'>
                    <a>Gegevens die we verzamelen of afleiden</a>
                  </Link>{' '}
                  van ons Privacybeleid voor eindgebruikers. Wanneer u niet
                  langer onze klant bent, blijven we uw informatie gebruiken en
                  delen zoals beschreven in deze kennisgeving.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Hoe?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Alle financiële bedrijven moeten persoonlijke informatie van
                  klanten delen om hun producten en diensten te kunnen leveren.
                  In het onderstaande gedeelte vermelden we de redenen waarom
                  financiële bedrijven de persoonlijke informatie van hun
                  consumenten kunnen delen; de redenen waarom Plaid ervoor kiest
                  om te delen; en of u dit delen kunt beperken.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Redenen waarom we uw persoonlijke informatie kunnen delen
                </strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>Deelt Plaid?</strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>Kunt u dit delen beperken?</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Voor onze dagelijkse zakelijke doeleinden –</strong>

                <span>
                  zoals het verbinden en delen van gegevens van uw financiële
                  rekeningen om de apps die u gebruikt aan te sturen, evenals om
                  te beschermen tegen fraude of misbruik, te reageren op
                  gerechtelijke bevelen en juridische onderzoeken, of te
                  rapporteren aan kredietbureaus.
                </span>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Ja</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nee</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Voor onze marketingdoeleinden </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nee</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>We delen niet </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Voor gezamenlijke marketing met andere financiële bedrijven
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nee</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>We delen niet </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Voor de dagelijkse zakelijke doeleinden van onze gelieerde
                  ondernemingen – informatie over transacties en ervaringen
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Ja</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Ja, en we delen alleen in uw opdracht </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Voor de dagelijkse zakelijke doeleinden van onze gelieerde
                  ondernemingen – informatie over kredietwaardigheid
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nee</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>We delen niet </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Voor niet-gelieerde bedrijven om aan u te verkopen
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nee</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>We delen niet </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Vraag</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  {' '}
                  Neem contact met ons op via{' '}
                  <Link href='mailto:privacy@plaid.com'>
                    <a>privacy@plaid.com</a>
                  </Link>{' '}
                  .
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Wie we zijn</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Wie verstrekt deze kennisgeving?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Plaid Inc.</span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Wat we doen</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Hoe beschermt Plaid mijn persoonlijke informatie?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Om uw persoonlijke informatie te beschermen tegen onbevoegde
                  toegang en gebruik, gebruiken we beveiligingsmaatregelen die
                  voldoen aan de federale wetgeving. Deze maatregelen omvatten
                  computerbeveiligingen en beveiligde bestanden en gebouwen.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Hoe verzamelt Plaid mijn persoonlijke informatie?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Zoals nader uitgelegd in het gedeelte{' '}
                  <Link href='#gegevens-wij-verzamelen-en-categorieën-bronnen'>
                    <a>Gegevens die we verzamelen of afleiden</a>
                  </Link>{' '}
                  van ons Privacybeleid voor eindgebruikers, verzamelen we
                  persoonlijke informatie die u aan ons verstrekt en verzamelen
                  we uw persoonlijke informatie van andere bedrijven wanneer u
                  onze producten of diensten gebruikt. Dit omvat bijvoorbeeld
                  wanneer u Plaid Portal gebruikt of wanneer u Plaid gebruikt om
                  uw gegevens te koppelen en te delen om de toepassingen
                  (“apps”) die u gebruikt aan te sturen.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Waarom kan ik het delen niet beperken?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  De federale wetgeving geeft u het recht om alleen het volgende
                  te beperken:
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    delen voor de dagelijkse zakelijke doeleinden van gelieerde
                    ondernemingen – informatie over uw kredietwaardigheid;
                  </span>
                </li>
                <li>
                  <span>
                    gelieerde ondernemingen die uw informatie gebruiken om aan u
                    te verkopen;
                  </span>
                </li>
                <li>
                  <span>
                    delen voor niet-gelieerde bedrijven om aan u te verkopen.
                  </span>
                </li>
              </ul>
              <p>
                <span></span>
              </p>
              <p>
                <span>
                  Staatswetten en individuele bedrijven kunnen u aanvullende
                  rechten geven om het delen te beperken. Zie hieronder voor
                  meer informatie over uw rechten onder de staatswetgeving.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Definities</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Gelieerde ondernemingen</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Ondernemingen die verbonden zijn door gemeenschappelijke
                  eigenaarschap of zeggenschap. Het kan gaan om financiële en
                  niet-financiële bedrijven.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    Onze gelieerde ondernemingen zijn Plaid Financial Ltd.,
                    Plaid B.V. en Plaid Consumer Reporting Agency Inc.
                    (“PlaidCRA”).
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Niet-gelieerde bedrijven</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Ondernemingen die niet verbonden zijn door gemeenschappelijke
                  eigenaarschap of zeggenschap. Het kan gaan om financiële en
                  niet-financiële bedrijven.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    Plaid deelt geen informatie met niet-gelieerde bedrijven
                    zodat zij u kunnen benaderen.
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Gezamenlijke marketing</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Een formele overeenkomst tussen niet-gelieerde financiële
                  ondernemingen die samen financiële producten of diensten aan u
                  verkopen.
                </span>
              </p>
              <ul>
                <li>
                  <span>Plaid doet niet aan gezamenlijke marketing.</span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>Andere belangrijke informatie </strong>
              </p>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>Californië: </strong>
                <span>
                  Als u een inwoner van Californië bent, zullen we geen
                  persoonlijke informatie die we over u verzamelen delen,
                  behalve voor zover toegestaan onder de wetgeving van
                  Californië.
                </span>
              </p>
              <p>
                <span></span>
              </p>
              <p>
                <strong>Vermont: </strong>
                <span>
                  Als u een inwoner van Vermont bent, zullen we geen
                  persoonlijke informatie die we over u verzamelen delen met
                  niet-gelieerde bedrijven, tenzij de wet dit toestaat of u
                  toestemming geeft.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <hr className='section-divider' />
    </>
  );
};

NlVersion.propTypes = propTypes;
export default NlVersion;
